import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
  Button,
} from '@mui/material';

import { getWeeklyGroupedScorecardReport } from '../../services/reportService';
import { getTimeZone } from '../../utils/timeCalculation';
import IssueBreakdownDialog from '../../pages/admin/developer-scorecard/IssueBreakdownDialog';

const WEEKS_PER_PAGE = 4;

const toFixed2 = (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : '0.00');

function MetricBox({ label, value, color }) {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 1.5,
        textAlign: 'center',
        borderRadius: 2,
        height: '100%',
      }}
    >
      <Typography variant="h6" fontWeight={700} color={color || 'text.primary'}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
}

function WeekCard({ group, onIssueClick }) {
  const user = group.users?.[0];
  if (!user) return null;

  const productivityPct = user.productivity?.productivityPercent ?? 0;
  const productivityColor =
    productivityPct >= 85 ? 'success.main' : productivityPct < 80 ? 'error.main' : 'text.primary';
  const totalIssues = user.quality?.totalIssues ?? 0;

  let weekNum = 'Not Submitted';
  let dateRange = null;
  if (group.periodKey) {
    weekNum = `Week ${group.periodKey.split('-')[1]}`;
    dateRange =
      group.weekStart && group.weekEnd
        ? `${format(new Date(group.weekStart), 'dd MMM')} – ${format(new Date(group.weekEnd), 'dd MMM yyyy')}`
        : group.weekLabel;
  }

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {weekNum}
          </Typography>
          {dateRange && (
            <Typography variant="caption" color="text.secondary">
              {dateRange}
            </Typography>
          )}
        </Box>

        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={3}>
            <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Tasks
              </Typography>
              <Typography variant="body2">Total: {user.taskSummary?.totalTasks ?? 0}</Typography>
              <Typography variant="body2">Done: {user.taskSummary?.completed ?? 0}</Typography>
              <Typography variant="body2">In Progress: {user.taskSummary?.inProgress ?? 0}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={3}>
            <MetricBox
              label="Productivity"
              value={`${toFixed2(productivityPct)}%`}
              color={productivityColor}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2, height: '100%', textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Hours
              </Typography>
              <Typography variant="body2">Planned: {toFixed2(user.productivity?.plannedHours)}</Typography>
              <Typography variant="body2">Productive: {toFixed2(user.productivity?.productiveHours)}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2, height: '100%', textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Issues
              </Typography>
              {totalIssues > 0 ? (
                <ButtonBase
                  onClick={() => onIssueClick(user, group)}
                  sx={{
                    typography: 'h6',
                    fontWeight: 700,
                    color: 'primary.main',
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                    borderRadius: 1,
                  }}
                >
                  {totalIssues}
                </ButtonBase>
              ) : (
                <Typography variant="h6" fontWeight={700}>
                  0
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default function ProfileScorecardSection({ dateRange, targetUserId }) {
  const [loadedGroups, setLoadedGroups] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasNext, setHasNext] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [issueDialogState, setIssueDialogState] = useState({ open: false, user: null, group: null });

  const abortControllerRef = useRef(null);
  const isFetchingRef = useRef(false);

  const filterKey = useMemo(
    () =>
      JSON.stringify({
        startDate: dateRange?.selection?.startDate,
        endDate: dateRange?.selection?.endDate,
        targetUserId,
      }),
    [dateRange, targetUserId]
  );

  const fetchPage = async (pageIdx) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (pageIdx === 0) {
      setIsLoading(true);
    } else {
      setIsFetchingMore(true);
    }

    try {
      const response = await getWeeklyGroupedScorecardReport(
        [], [], [], [],
        [targetUserId],
        '',
        dateRange,
        [], [],
        getTimeZone(),
        {
          weekStart: pageIdx * WEEKS_PER_PAGE,
          weeksPerPage: WEEKS_PER_PAGE,
          globalFilter: '',
          submissionStatus: 'filled',
        },
        controller.signal
      );

      if (controller.signal.aborted) return;

      const groups = response?.groups ?? [];
      const next = response?.hasNext ?? false;
      const nextPageIdx = pageIdx + 1;

      if (pageIdx === 0) {
        setLoadedGroups(groups);
      } else {
        setLoadedGroups((prev) => {
          const existingKeys = new Set(prev.map((g) => g.periodKey));
          const deduped = groups.filter((g) => !existingKeys.has(g.periodKey));
          return [...prev, ...deduped];
        });
      }

      setHasNext(next);
      setPageIndex(nextPageIdx);

      isFetchingRef.current = false;
      setIsLoading(false);
      setIsFetchingMore(false);

      if (next && groups.length === 0) {
        fetchPage(nextPageIdx);
      }
    } catch (err) {
      isFetchingRef.current = false;
      setIsLoading(false);
      setIsFetchingMore(false);
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      console.error('Scorecard fetch error:', err);
      setHasNext(false);
    }
  };

  useEffect(() => {
    setLoadedGroups([]);
    setPageIndex(0);
    setHasNext(null);
    isFetchingRef.current = false;
    fetchPage(0);

    return () => {
      abortControllerRef.current?.abort();
    };
    // filterKey encodes dateRange + targetUserId — safe to use as dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const handleIssueClick = (user, group) => {
    setIssueDialogState({ open: true, user, group });
  };

  const handleCloseDialog = () => {
    setIssueDialogState({ open: false, user: null, group: null });
  };

  const { user: dialogUser, group: dialogGroup } = issueDialogState;

  const dateRangeLabel =
    dialogGroup?.weekStart && dialogGroup?.weekEnd
      ? `${format(new Date(dialogGroup.weekStart), 'dd MMM')} – ${format(new Date(dialogGroup.weekEnd), 'dd MMM yyyy')}`
      : dialogGroup?.weekLabel || '';

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="h6" gutterBottom>
        My Scorecard
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Weekly performance based on selected date range
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading scorecard...</Typography>
        </Box>
      )}

      {!isLoading && loadedGroups.length === 0 && hasNext === false && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No scorecard data for the selected period.</Typography>
        </Box>
      )}

      {!isLoading &&
        loadedGroups.map((group, idx) => (
          <WeekCard key={group.periodKey || `not-submitted-${idx}`} group={group} onIssueClick={handleIssueClick} />
        ))}

      {isFetchingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!isFetchingMore && hasNext === true && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button variant="outlined" onClick={() => fetchPage(pageIndex)}>
            Load more
          </Button>
        </Box>
      )}

      <IssueBreakdownDialog
        open={issueDialogState.open}
        onClose={handleCloseDialog}
        developerName={dialogUser?.userName}
        dateRangeLabel={dateRangeLabel}
        totalIssues={dialogUser?.quality?.totalIssues}
        issuesByCategory={dialogUser?.quality?.issuesByCategory}
      />
    </Box>
  );
}
